/**
 * mentionUtils.ts — Consolidated mention utilities for canvas-v2
 *
 * Provides shared helpers for building mention candidate lists from
 * canvas nodes. Used by RichTextBox (@ mention dropdown) and
 * ReferenceSelector (asset grid).
 *
 * Node data access is narrowed through generated node data helpers.
 * Port name mismatches are caught at compile time.
 */

import {
  FE_TYPE_TO_BACKEND,
  NODE_SCHEMAS_BY_TYPE,
  YDOC_NODE_FIELDS,
  getTypedNodeData,
  isTypedReactFlowNode,
  type AppNodeData,
  type AudioAssetRefDict,
  type GeneratedAudioWithHistory,
  type GeneratedImageWithHistory,
  type GeneratedVideoWithHistory,
  type ImageAssetRefDict,
  type SoundGeneratedAudioDict,
  type UntypedReactFlowNode,
  type VideoAssetRefDict,
} from '../../../../generated/nodeDataTypes'
import { nodeSchemas } from '../../nodeRegistry'
import { extractNodeAssetInfo } from '../../../../utils/buildDownloadFilename'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'
import {
  getDesignerMainImage,
  type DesignerImageOutput,
} from './designerUtils'
import { resolvePickedVideoAsset } from './pickedVideoAsset'
import type { VideoHistoryEntry } from './variantOrdinals'
import {
  CHARACTER_VOICE_REFERENCE_PAIR_KIND,
  type ReferencePairKind,
} from '../../referencePairEdges'

// Feature tag: library-display. The asset-manager library grid (and any
// other surface that flows through getImagePath for designer nodes)
// reaches in here to resolve the thumbnail src. Logging the resolved
// index + path makes the end-to-end picked-variant trace visible.
const libraryDisplayLog = createDebug('dw:library-display:thumbnail')
const audioReferenceLabelLog = createNadDebug('dw:reference-selector:audio-label')
const visibleAudioReferenceLabelLog = createNadDebug('dw:audio-reference-labels')
const CHARACTER_VOICE_SOURCE_OWNER_FIELD = 'in_character_voice_source_for'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface MentionCandidate {
  id: string
  type: string
  label: string
  icon: string
  thumbnailPath?: string
}

export type MediaType = 'image' | 'video' | 'audio'

export interface CanvasAssetCandidate {
  nodeId: string
  nodeType: string
  label: string
  icon: string
  assetPath: string
  mediaType: MediaType
  /** Exact schema output port that owns this asset. */
  sourceHandle?: string
  /** Item index when the source port is an array output. */
  fromIndex?: number
  /** Variant history for nodes that have one (video / image / audio generators, designers, sound expert). */
  variantHistory?: VideoHistoryEntry[]
  /** Picked variant index inside variantHistory. */
  pickedIndex?: number
  /**
   * Non-media kind for items with no thumbnail/asset (e.g. a saved script).
   * Only the asset-manager library browse view emits these (via
   * buildAssetCandidates(..., includeNonMedia=true)); reference-input and
   * download paths never produce them, so mediaType stays the narrow MediaType.
   * When set, ``mediaType`` is a placeholder ('image') and the picker renders
   * ``textPreview`` instead of a thumbnail.
   */
  nonMediaKind?: 'script'
  /** Short text preview rendered for nonMediaKind items. */
  textPreview?: string
}

type MentionNode = UntypedReactFlowNode
type AssetHistoryEntryLike = { result?: { local_path?: string } } | null
type AssetHistoryPortLike = {
  history?: AssetHistoryEntryLike[]
  picked_index?: number
}
type AssetHistoryPortValue =
  | AudioAssetRefDict
  | GeneratedAudioWithHistory
  | GeneratedImageWithHistory
  | GeneratedVideoWithHistory
  | ImageAssetRefDict
  | SoundGeneratedAudioDict
  | VideoAssetRefDict

export interface CanvasAssetEdge {
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
  data?: {
    toIndex?: number
    fromIndex?: number
    referencePairKind?: ReferencePairKind
  } | null
}

export interface CanvasAssetEdgeUpdate {
  source: string
  sourceHandle: string
  toIndex: number
  fromIndex?: number
  referencePairKind?: ReferencePairKind
}

// ─── Node Image Path Extractors ──────────────────────────────────────────────

// Designer nodes (character / location / object) store the user's picked
// variant in the historical asset wrapper's `main.picked_index`.
function resolveDesignerHistoryPath(
  history: AssetHistoryEntryLike[] | undefined,
  mainPickedIndex: number | undefined,
  nodeId: string,
  nodeType: string,
): string | undefined {
  if (!history?.length) return undefined
  const idx = mainPickedIndex ?? 0
  const resolved = history[idx]?.result?.local_path
  libraryDisplayLog('designer_history_resolved', {
    nodeId,
    nodeType,
    picked_index: mainPickedIndex,
    resolved_index: idx,
    history_length: history.length,
    resolved_path: resolved,
  })
  return resolved
}

function resolveDesignerImagePath(
  outputObj: DesignerImageOutput | undefined,
  nodeId: string,
  nodeType: string,
): string | undefined {
  const main = getDesignerMainImage(outputObj)
  if (!main) return undefined
  const history = main?.history
  if (!history?.length) return undefined
  return resolveDesignerHistoryPath(history, main.picked_index, nodeId, nodeType)
}

function resolvePickedAssetPath(asset: AssetHistoryPortValue | AssetHistoryPortLike | null | undefined): string | undefined {
  const history = asset?.history ?? []
  if (!history.length) return undefined
  return history[asset?.picked_index ?? 0]?.result?.local_path
}

function getImagePath(node: MentionNode): string | undefined {
  if (isTypedReactFlowNode(node, 'characterDesigner')) {
    return resolveDesignerImagePath(node.data.out_image, node.id, 'characterDesigner')
  }

  if (isTypedReactFlowNode(node, 'locationDesigner')) {
    return resolveDesignerImagePath(node.data.out_image, node.id, 'locationDesigner')
  }

  if (isTypedReactFlowNode(node, 'objectDesigner')) {
    return resolveDesignerImagePath(node.data.out_image, node.id, 'objectDesigner')
  }

  if (isTypedReactFlowNode(node, 'imageGenerator')) {
    return resolvePickedAssetPath(node.data.out_image)
  }

  if (isTypedReactFlowNode(node, 'image')) {
    return resolvePickedAssetPath(node.data.out_image)
  }

  if (isTypedReactFlowNode(node, 'storyboardNoEnhanceDesigner')) {
    return resolvePickedAssetPath(node.data.out_image)
  }

  if (isTypedReactFlowNode(node, 'webtoonPanelGenerator')) {
    return resolvePickedAssetPath(node.data.out_image)
  }

  return undefined
}

const IMAGE_NODE_TYPES = new Set([
  'characterDesigner',
  'locationDesigner',
  'objectDesigner',
  'imageGenerator',
  'image',
  'storyboardNoEnhanceDesigner',
  'webtoonPanelGenerator',
])

// ─── Node Audio Path Extractors ──────────────────────────────────────────────

function getAudioPath(node: MentionNode): string | undefined {
  if (isTypedReactFlowNode(node, 'audio')) {
    return resolvePickedAssetPath(node.data.out_audio)
  }

  if (isTypedReactFlowNode(node, 'soundExpert')) {
    return resolvePickedAssetPath(node.data.out_audio)
  }

  return undefined
}

const AUDIO_NODE_TYPES = new Set(['audio', 'soundExpert'])

// ─── Node Script/Text Preview Extractors ─────────────────────────────────────
//
// Script nodes have no media asset, so the asset-manager library grid used to
// drop them entirely (invisible). For the library browse surface we surface a
// short text preview so saved scripts are visible and disambiguable.

const SCRIPT_NODE_TYPES = new Set(['scriptWriter'])

function getScriptPreview(node: MentionNode): string | undefined {
  if (isTypedReactFlowNode(node, 'scriptWriter')) {
    const raw = node.data.out_script
    if (typeof raw === 'string' && raw.trim()) {
      return raw.trim().replace(/\s+/g, ' ').slice(0, 140)
    }
  }
  return undefined
}

export function getAudioAssetPath(
  nodeType: string,
  data: AppNodeData | undefined,
): string | undefined {
  if (!data) return undefined

  const soundExpertData = getTypedNodeData({ type: nodeType, data }, 'soundExpert')
  if (soundExpertData) {
    return resolvePickedAssetPath(soundExpertData.out_audio)
  }

  const audioData = getTypedNodeData({ type: nodeType, data }, 'audio')
  if (audioData) return resolvePickedAssetPath(audioData.out_audio)

  return undefined
}

// ─── Core Functions ──────────────────────────────────────────────────────────

function compactLabel(value: string): string {
  return value.length > 30 ? value.slice(0, 30) + '...' : value
}

function firstLabel(...values: Array<string | undefined>): string | undefined {
  for (const value of values) {
    const trimmed = value?.trim()
    if (trimmed) return trimmed
  }
  return undefined
}

function clipNumberLabel(clipNumber?: number): string | undefined {
  return typeof clipNumber === 'number' && Number.isFinite(clipNumber) && clipNumber > 0
    ? `Clip ${clipNumber}`
    : undefined
}

function fileNameFromAssetPath(path: string): string | undefined {
  const filename = path.split('/').pop()?.trim()
  return filename || undefined
}

export function getAudioReferenceLabel(node: MentionNode, path: string): string {
  let label: string | undefined
  let source = 'node'

  if (isTypedReactFlowNode(node, 'soundExpert')) {
    const outputVoice = node.data.out_voice_profile
    const inputVoice = node.data.in_voice_profile
    label = firstLabel(
      node.data.in_name,
      outputVoice?.voice_name,
      outputVoice?.name,
      inputVoice?.voice_name,
      inputVoice?.name,
      node.data.label,
      fileNameFromAssetPath(path),
    )
    if (label === node.data.in_name) source = 'in_name'
    else if (label === outputVoice?.voice_name || label === outputVoice?.name) source = 'out_voice_profile'
    else if (label === inputVoice?.voice_name || label === inputVoice?.name) source = 'in_voice_profile'
    else if (label === node.data.label) source = 'label'
    else source = 'filename'
  } else if (isTypedReactFlowNode(node, 'audio')) {
    label = firstLabel(
      node.data.label,
      node.data.in_file_name,
      fileNameFromAssetPath(path),
    )
    if (label === node.data.label) source = 'label'
    else if (label === node.data.in_file_name) source = 'in_file_name'
    else source = 'filename'
  }

  const resolved = label ?? getNodeLabel(node)
  audioReferenceLabelLog({
    event: 'audio-reference-label:resolved',
    nodeId: node.id,
    nodeType: node.type ?? '',
    source,
    label: resolved,
    assetPath: path,
  })
  return resolved
}

export function getNodeLabel(node: MentionNode): string {
  switch (node.type) {
    case 'characterDesigner': {
      if (isTypedReactFlowNode(node, 'characterDesigner')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
      }
      break
    }
    case 'imageGenerator': {
      if (isTypedReactFlowNode(node, 'imageGenerator')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
        const prompt = firstLabel(node.data.in_prompt)
        if (prompt) return compactLabel(prompt)
      }
      break
    }
    case 'locationDesigner': {
      if (isTypedReactFlowNode(node, 'locationDesigner')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
      }
      break
    }
    case 'nodeGroup':
    case 'shortVideoGroup':
    case 'webtoonGroup': {
      if (
        isTypedReactFlowNode(node, 'nodeGroup') ||
        isTypedReactFlowNode(node, 'shortVideoGroup') ||
        isTypedReactFlowNode(node, 'webtoonGroup')
      ) {
        const label = firstLabel(node.data.in_label, node.data.label)
        if (label) return label
      }
      break
    }
    case 'objectDesigner': {
      if (isTypedReactFlowNode(node, 'objectDesigner')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
      }
      break
    }
    case 'scriptWriter': {
      if (isTypedReactFlowNode(node, 'scriptWriter')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
      }
      break
    }
    case 'soundExpert': {
      if (isTypedReactFlowNode(node, 'soundExpert')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
      }
      break
    }
    case 'image': {
      if (isTypedReactFlowNode(node, 'image')) {
        const label = firstLabel(node.data.label, node.data.in_file_name)
        if (label) return label
      }
      break
    }
    case 'video': {
      if (isTypedReactFlowNode(node, 'video')) {
        const label = firstLabel(node.data.label, node.data.in_file_name)
        if (label) return label
      }
      break
    }
    case 'audio': {
      if (isTypedReactFlowNode(node, 'audio')) {
        const label = firstLabel(node.data.label, node.data.in_file_name)
        if (label) return label
      }
      break
    }
    case 'videoGenerator': {
      if (isTypedReactFlowNode(node, 'videoGenerator')) {
        const label = firstLabel(
          node.data.in_name,
          clipNumberLabel(node.data.in_clip_number),
          node.data.label,
        )
        if (label) return label
      }
      break
    }
    case 'webtoonPanelGenerator': {
      if (isTypedReactFlowNode(node, 'webtoonPanelGenerator')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
      }
      break
    }
  }

  const schema = node.type ? nodeSchemas[node.type] : undefined
  if (schema) return schema.label
  return node.type ?? node.id
}

export function getNodeIcon(nodeType: string): string {
  const schema = nodeSchemas[nodeType]
  return schema?.icon ?? ''
}

export function getNodeThumbnailPath(node: MentionNode): string | undefined {
  if (!IMAGE_NODE_TYPES.has(node.type ?? '')) return undefined
  return getImagePath(node)
}

export function buildCandidates(nodes: MentionNode[]): MentionCandidate[] {
  return nodes
    .filter((node) => node.type !== 'director')
    .map((node) => ({
      id: node.id,
      type: node.type ?? '',
      label: getNodeLabel(node),
      icon: getNodeIcon(node.type ?? ''),
      thumbnailPath: getNodeThumbnailPath(node),
    }))
}

export function buildAssetCandidates(
  nodes: MentionNode[],
  excludeNodeId?: string,
  /**
   * Include non-media items (e.g. scripts) that have no asset path. Defaults to
   * false so reference-input pickers keep their media-only behaviour. The
   * library asset-manager browse view passes true so saved scripts are visible.
   */
  includeNonMedia = false,
  dedupeStoryboardGridPreviews = true,
): CanvasAssetCandidate[] {
  const results: CanvasAssetCandidate[] = []

  for (const node of nodes) {
    if (node.id === excludeNodeId) continue
    const nodeType = node.type ?? ''

    // StoryboardDesigner is a workflow/container node, not a single image
    // reference. Its schema exposes the actual per-grid images as an indexed
    // array output. Emit one candidate per grid so 27 panels surface as three
    // distinct 9-grid references instead of one misleading aggregate card.
    if (isTypedReactFlowNode(node, 'storyboardDesigner')) {
      const storyboardLabel = getNodeLabel(node)
      for (const [fromIndex, gridImage] of (node.data.out_grid_images ?? []).entries()) {
        const path = resolvePickedAssetPath(gridImage)
        if (!path) continue
        results.push({
          nodeId: node.id,
          nodeType,
          label: `${storyboardLabel} · Grid ${fromIndex + 1}`,
          icon: getNodeIcon(nodeType),
          assetPath: path,
          mediaType: 'image',
          sourceHandle: 'out_grid_images',
          fromIndex,
        })
      }
    }

    if (IMAGE_NODE_TYPES.has(nodeType)) {
      const path = getImagePath(node)
      if (path) {
        results.push({
          nodeId: node.id,
          nodeType,
          label: getNodeLabel(node),
          icon: getNodeIcon(nodeType),
          assetPath: path,
          mediaType: 'image',
          sourceHandle: getImageOutputPort(nodeType),
        })
      }
    }

    const pickedVideo = resolvePickedVideoAsset(node)
    if (pickedVideo) {
      results.push({
        nodeId: node.id,
        nodeType,
        label: getNodeLabel(node),
        icon: getNodeIcon(nodeType),
        assetPath: pickedVideo.path,
        mediaType: 'video',
        sourceHandle: getVideoOutputPort(nodeType),
      })
    }

    if (AUDIO_NODE_TYPES.has(nodeType)) {
      const path = getAudioPath(node)
      if (path) {
        results.push({
          nodeId: node.id,
          nodeType,
          label: getAudioReferenceLabel(node, path),
          icon: getNodeIcon(nodeType),
          assetPath: path,
          mediaType: 'audio',
          sourceHandle: getAudioOutputPort(nodeType),
        })
      }
    }

    if (includeNonMedia && SCRIPT_NODE_TYPES.has(nodeType)) {
      results.push({
        nodeId: node.id,
        nodeType,
        label: getNodeLabel(node),
        icon: getNodeIcon(nodeType),
        assetPath: '',
        // No real media; 'image' is an inert placeholder. The card renders
        // textPreview because nonMediaKind is set.
        mediaType: 'image',
        nonMediaKind: 'script',
        textPreview: getScriptPreview(node),
      })
    }
  }

  if (!dedupeStoryboardGridPreviews) return results

  const storyboardGridPaths = new Set(
    results
      .filter((candidate) => candidate.sourceHandle === 'out_grid_images')
      .map((candidate) => candidate.assetPath),
  )
  return results.filter((candidate) => (
    candidate.sourceHandle === 'out_grid_images'
    || !storyboardGridPaths.has(candidate.assetPath)
  ))
}

function compareTargetIndex(left: number | undefined, right: number | undefined): number {
  const normalizedLeft = left ?? Number.MAX_SAFE_INTEGER
  const normalizedRight = right ?? Number.MAX_SAFE_INTEGER
  return normalizedLeft - normalizedRight
}

export function buildAssetReferencesFromEdges(
  nodes: MentionNode[],
  edges: CanvasAssetEdge[],
  targetId: string,
  targetHandle: string,
  mediaType?: MediaType,
): CanvasAssetCandidate[] {
  const candidates = buildAssetCandidates(nodes, targetId, false, false)
    .filter((candidate) => mediaType === undefined || candidate.mediaType === mediaType)
  if (mediaType === undefined) {
    return edges.filter(edge => edge.target === targetId && edge.targetHandle === targetHandle)
      .sort((left, right) => compareTargetIndex(left.data?.toIndex, right.data?.toIndex))
      .flatMap(edge => candidates.filter(candidate => candidate.nodeId === edge.source
        && candidate.sourceHandle === edge.sourceHandle
        && (edge.data?.fromIndex === undefined || candidate.fromIndex === edge.data.fromIndex)))
  }
  const candidateForEdge = (edge: CanvasAssetEdge): CanvasAssetCandidate | undefined =>
    candidates.find((candidate) => (
      candidate.nodeId === edge.source
      && candidate.sourceHandle === edge.sourceHandle
      && candidate.fromIndex === edge.data?.fromIndex
    ))
      ?? candidates.find((candidate) => (
        candidate.nodeId === edge.source
        && candidate.fromIndex === undefined
      ))

  return edges
    .filter((edge) => edge.target === targetId && edge.targetHandle === targetHandle)
    .sort((left, right) => compareTargetIndex(left.data?.toIndex, right.data?.toIndex))
    .map(candidateForEdge)
    .filter((candidate): candidate is CanvasAssetCandidate => candidate !== undefined)
}

function linkedCharacterVoiceSoundNodeId(characterNodeId: string): string {
  return `${characterNodeId}__voice_sound`
}

function isCharacterVoiceSourceNode(node: MentionNode | undefined): node is MentionNode {
  return node?.type === 'soundExpert' || node?.type === 'audio'
}

function isActiveCharacterVoiceSourceNode(
  characterNodeId: string,
  node: MentionNode | undefined,
): node is MentionNode {
  if (node?.type === 'audio') return true
  if (node?.type !== 'soundExpert') return false
  if (node.id === linkedCharacterVoiceSoundNodeId(characterNodeId)) return true
  const soundData = getTypedNodeData(node, 'soundExpert')
  return soundData?.[CHARACTER_VOICE_SOURCE_OWNER_FIELD] === characterNodeId
}

function activeCharacterVoiceSourceNodeIds(
  characterNodeId: string,
  nodesById: Map<string, MentionNode>,
  edges: CanvasAssetEdge[],
): string[] {
  const seen = new Set<string>()
  const results: string[] = []
  const add = (nodeId: string) => {
    if (seen.has(nodeId)) return
    const node = nodesById.get(nodeId)
    if (!isActiveCharacterVoiceSourceNode(characterNodeId, node)) return
    seen.add(nodeId)
    results.push(nodeId)
  }

  for (const edge of edges) {
    if (
      edge.source === characterNodeId &&
      edge.sourceHandle === 'out_voice_profile' &&
      edge.targetHandle === 'in_voice_profile'
    ) {
      add(edge.target)
    }
  }

  if (results.length === 0) add(linkedCharacterVoiceSoundNodeId(characterNodeId))
  return results
}

export function resolveCharacterOwnedAudioReferenceLabels(
  nodes: MentionNode[],
  edges: CanvasAssetEdge[],
  refs: CanvasAssetCandidate[],
): CanvasAssetCandidate[] {
  const nodesById = new Map(nodes.map((node) => [node.id, node]))
  const characterNameByVoiceNodeId = new Map<string, string>()

  for (const edge of edges) {
    if (
      edge.sourceHandle !== 'out_voice_profile' ||
      edge.targetHandle !== 'in_voice_profile'
    ) {
      continue
    }

    const characterNode = nodesById.get(edge.source)
    const voiceNode = nodesById.get(edge.target)
    if (
      !characterNode ||
      !voiceNode ||
      characterNode.type !== 'characterDesigner' ||
      !isActiveCharacterVoiceSourceNode(edge.source, voiceNode)
    ) {
      continue
    }

    characterNameByVoiceNodeId.set(voiceNode.id, getNodeLabel(characterNode))
  }

  for (const node of nodes) {
    if (node.type !== 'characterDesigner') continue
    const deterministicSoundNodeId = linkedCharacterVoiceSoundNodeId(node.id)
    const soundNode = nodesById.get(deterministicSoundNodeId)
    if (soundNode?.type === 'soundExpert' && !characterNameByVoiceNodeId.has(deterministicSoundNodeId)) {
      characterNameByVoiceNodeId.set(deterministicSoundNodeId, getNodeLabel(node))
    }
  }

  if (characterNameByVoiceNodeId.size === 0) return refs

  return refs.map((ref) => {
    if (ref.mediaType !== 'audio' || (ref.nodeType !== 'soundExpert' && ref.nodeType !== 'audio')) return ref
    const characterName = characterNameByVoiceNodeId.get(ref.nodeId)
    if (!characterName) return ref
    visibleAudioReferenceLabelLog({
      event: 'audio-reference-labels:character-owned-label-resolved',
      voice_node_id: ref.nodeId,
      voice_node_type: ref.nodeType,
      character_label: characterName,
      previous_label: ref.label,
    })
    return { ...ref, label: characterName }
  })
}

export function buildCharacterVoiceAudioReferences(
  refs: Array<Pick<CanvasAssetCandidate, 'nodeId' | 'nodeType'>>,
  nodes: MentionNode[],
  edges: CanvasAssetEdge[],
): CanvasAssetCandidate[] {
  const nodesById = new Map(nodes.map((node) => [node.id, node]))
  const results: CanvasAssetCandidate[] = []
  const seen = new Set<string>()

  for (const ref of refs) {
    if (ref.nodeType !== 'characterDesigner') continue
    const characterNode = nodesById.get(ref.nodeId)
    if (!characterNode || characterNode.type !== 'characterDesigner') continue
    const characterLabel = getNodeLabel(characterNode)

    for (const voiceNodeId of activeCharacterVoiceSourceNodeIds(ref.nodeId, nodesById, edges)) {
      const voiceNode = nodesById.get(voiceNodeId)
      if (!isCharacterVoiceSourceNode(voiceNode)) continue
      const assetPath = getAudioPath(voiceNode)
      if (!assetPath) continue
      const sourceHandle = getAudioOutputPort(voiceNode.type ?? '')
      const key = `${voiceNode.id}|${sourceHandle}`
      if (seen.has(key)) continue
      seen.add(key)
      results.push({
        nodeId: voiceNode.id,
        nodeType: voiceNode.type ?? '',
        label: characterLabel,
        icon: getNodeIcon(voiceNode.type ?? ''),
        assetPath,
        mediaType: 'audio',
        sourceHandle,
      })
    }
  }

  return results
}

export function buildImageReferenceEdgeUpdates(
  refs: Array<Pick<CanvasAssetCandidate, 'nodeId' | 'nodeType' | 'assetPath' | 'sourceHandle' | 'fromIndex'>>,
): CanvasAssetEdgeUpdate[] {
  const invalid = refs.find((ref) => !ref.nodeId || ref.nodeId.startsWith('ref-'))
  if (invalid) {
    throw new Error(
      `Reference selector cannot save ${invalid.assetPath} without a backing canvas node edge`,
    )
  }

  return refs.map((ref, index) => ({
    source: ref.nodeId,
    sourceHandle: ref.sourceHandle ?? getImageOutputPort(ref.nodeType),
    toIndex: index,
    ...(ref.fromIndex === undefined ? {} : { fromIndex: ref.fromIndex }),
  }))
}

/** Reconcile picker-owned character voices while preserving other voice inputs. */
export function mergeCharacterVoiceReferenceEdgeUpdates(
  refs: Array<Pick<CanvasAssetCandidate, 'nodeId' | 'nodeType' | 'sourceHandle' | 'fromIndex'>>,
  edges: CanvasAssetEdge[],
  targetId: string,
): CanvasAssetEdgeUpdate[] {
  const selectedPickerVoiceKeys = new Set(
    refs
      .filter((ref) => ref.nodeType === 'characterDesigner' || ref.nodeType === 'soundExpert')
      .map((ref) => `${ref.nodeId}|${ref.sourceHandle ?? 'out_voice_profile'}|${ref.fromIndex ?? ''}`),
  )
  const updates: Array<Omit<CanvasAssetEdgeUpdate, 'toIndex'>> = []
  const existing = new Map<string, number>()

  const currentEdges = edges
    .filter((edge): edge is CanvasAssetEdge & { sourceHandle: string } => (
      edge.target === targetId
      && edge.targetHandle === 'in_voice_profiles'
      && typeof edge.sourceHandle === 'string'
    ))
    .sort((left, right) => compareTargetIndex(left.data?.toIndex, right.data?.toIndex))

  for (const edge of currentEdges) {
    const isPickerOwned = edge.data?.referencePairKind === CHARACTER_VOICE_REFERENCE_PAIR_KIND
    const key = `${edge.source}|${edge.sourceHandle}|${edge.data?.fromIndex ?? ''}`
    if (isPickerOwned && !selectedPickerVoiceKeys.has(key)) continue

    const duplicateIndex = existing.get(key)
    if (duplicateIndex !== undefined) {
      // A non-picker edge must win so deselecting a visual reference never
      // removes voice wiring owned by dialogue or narration.
      if (!isPickerOwned) delete updates[duplicateIndex].referencePairKind
      continue
    }

    existing.set(key, updates.length)
    updates.push({
      source: edge.source,
      sourceHandle: edge.sourceHandle,
      ...(edge.data?.fromIndex === undefined ? {} : { fromIndex: edge.data.fromIndex }),
      ...(isPickerOwned ? { referencePairKind: CHARACTER_VOICE_REFERENCE_PAIR_KIND } : {}),
    })
  }

  for (const ref of refs) {
    if (ref.nodeType !== 'characterDesigner' && ref.nodeType !== 'soundExpert') continue
    const sourceHandle = ref.sourceHandle ?? 'out_voice_profile'
    const key = `${ref.nodeId}|${sourceHandle}|${ref.fromIndex ?? ''}`
    if (existing.has(key)) continue
    existing.set(key, updates.length)
    updates.push({
      source: ref.nodeId,
      sourceHandle,
      ...(ref.fromIndex === undefined ? {} : { fromIndex: ref.fromIndex }),
      referencePairKind: CHARACTER_VOICE_REFERENCE_PAIR_KIND,
    })
  }

  return updates.map((edge, toIndex) => ({ ...edge, toIndex }))
}

export function buildVideoReferenceEdgeUpdates(
  refs: Array<Pick<CanvasAssetCandidate, 'nodeId' | 'nodeType' | 'assetPath' | 'sourceHandle' | 'fromIndex'>>,
): CanvasAssetEdgeUpdate[] {
  const invalid = refs.find((ref) => !ref.nodeId || ref.nodeId.startsWith('ref-'))
  if (invalid) {
    throw new Error(
      `Reference selector cannot save ${invalid.assetPath} without a backing canvas node edge`,
    )
  }

  return refs.map((ref, index) => ({
    source: ref.nodeId,
    sourceHandle: ref.sourceHandle ?? getVideoOutputPort(ref.nodeType),
    toIndex: index,
    ...(ref.fromIndex === undefined ? {} : { fromIndex: ref.fromIndex }),
  }))
}

/**
 * Edge updates for `in_reference_audios`.
 *
 * Sibling of buildVideoReferenceEdgeUpdates: same "must have a backing canvas
 * node" invariant, resolving the source handle through getAudioOutputPort so a
 * sound_expert and a raw audio node both wire correctly.
 */
export function buildAudioReferenceEdgeUpdates(
  refs: Array<Pick<CanvasAssetCandidate, 'nodeId' | 'nodeType' | 'assetPath'>>,
): CanvasAssetEdgeUpdate[] {
  const invalid = refs.find((ref) => !ref.nodeId || ref.nodeId.startsWith('ref-'))
  if (invalid) {
    throw new Error(
      `Reference selector cannot save ${invalid.assetPath} without a backing canvas node edge`,
    )
  }

  return refs.map((ref, index) => ({
    source: ref.nodeId,
    sourceHandle: getAudioOutputPort(ref.nodeType),
    toIndex: index,
  }))
}

/**
 * Build ALL downloadable asset candidates from canvas nodes.
 * Unlike buildAssetCandidates (which returns one asset per node),
 * this returns multiple assets per node (e.g. all storyboard panel images).
 */
export function buildDownloadCandidates(
  nodes: MentionNode[]
): CanvasAssetCandidate[] {
  const results: CanvasAssetCandidate[] = []

  for (const node of nodes) {
    const nodeType = node.type ?? ''
    const label = getNodeLabel(node)
    const icon = getNodeIcon(nodeType)

    // Single dispatcher: returns mediaType + assetPath + variantHistory + pickedIndex.
    // Shared with per-node header download handlers so bulk + header agree on filenames.
    const info = extractNodeAssetInfo(node)
    if (info) {
      results.push({
        nodeId: node.id,
        nodeType,
        label,
        icon,
        assetPath: info.assetPath,
        mediaType: info.mediaType,
        variantHistory: info.variantHistory,
        pickedIndex: info.pickedIndex,
      })
    }
  }

  return results
}

export function getImageOutputPort(nodeType: string): string {
  const schema = nodeSchemas[nodeType]
  if (!schema) return 'out_image'
  const hasImagePath = schema.outputs?.some((p: { id: string }) => p.id === 'out_image')
  if (hasImagePath) return 'out_image'
  const port = schema.outputs?.find((p: { type: string }) => p.type === 'image')
  return port?.id ?? 'out_image'
}

export function getVideoOutputPort(nodeType: string): string {
  if (nodeType === 'videoGenerator') return 'out_video'
  if (nodeType === 'video') return 'out_video'
  if (nodeType === 'videoEditor') return 'out_video'

  const schema = nodeSchemas[nodeType]
  const preferred = schema?.outputs?.find((p: { id: string }) => p.id === 'out_video')
  if (preferred) return preferred.id
  const port = schema?.outputs?.find((p: { type: string }) => p.type === 'video')
  return port?.id ?? 'out_video'
}

export function getAudioOutputPort(nodeType: string): string {
  if (nodeType === 'soundExpert') return 'out_audio'
  if (nodeType === 'audio') return 'out_audio'

  const schema = nodeSchemas[nodeType]
  const preferred = schema?.outputs?.find((p: { id: string }) => p.id === 'out_audio')
  if (preferred) return preferred.id
  const port = schema?.outputs?.find((p: { type: string }) => p.type === 'audio')
  return port?.id ?? 'out_audio'
}

/** A reference is an output/array element, not merely a source node. */
export function referenceMediaKey(ref: Pick<CanvasAssetCandidate, 'nodeId' | 'mediaType' | 'sourceHandle' | 'fromIndex'>): string {
  return `${ref.mediaType}:${ref.nodeId}:${ref.sourceHandle ?? ''}:${ref.fromIndex ?? ''}`
}

export function buildOrderedReferenceMedia(
  nodes: MentionNode[], edges: CanvasAssetEdge[], targetId: string,
  legacyPorts: Array<[string, MediaType]>,
): CanvasAssetCandidate[] {
  if (edges.some(edge => edge.target === targetId && edge.targetHandle === 'in_reference_media')) {
    return buildAssetReferencesFromEdges(nodes, edges, targetId, 'in_reference_media')
  }
  return legacyPorts.flatMap(([port, media]) => buildAssetReferencesFromEdges(nodes, edges, targetId, port, media))
}

/** Keep unresolved edge slots visible to validation instead of skipping to a later video. */
export function buildReferenceMediaOrder(
  nodes: MentionNode[], edges: CanvasAssetEdge[], targetId: string,
): Array<MediaType | null> {
  const ports = edges.some(edge => edge.target === targetId && edge.targetHandle === 'in_reference_media')
    ? ['in_reference_media'] : ['in_images', 'in_reference_videos', 'in_reference_audios']
  const candidates = buildAssetCandidates(nodes, targetId, false, false)
  return ports.flatMap(port => edges
    .filter(edge => edge.target === targetId && edge.targetHandle === port)
    .sort((a, b) => compareTargetIndex(a.data?.toIndex, b.data?.toIndex))
    .flatMap((edge): Array<MediaType | null> => {
      const refs = candidates.filter(ref => ref.nodeId === edge.source
        && ref.sourceHandle === edge.sourceHandle
        && (edge.data?.fromIndex === undefined || ref.fromIndex === edge.data.fromIndex))
      const source = nodes.find(node => node.id === edge.source)
      if (source && isTypedReactFlowNode(source, 'storyboardDesigner')
        && edge.sourceHandle === 'out_grid_images' && edge.data?.fromIndex === undefined) {
        const slots = (source.data.out_grid_images ?? []).map((_, index) =>
          refs.find(ref => ref.fromIndex === index)?.mediaType ?? null)
        return slots.length ? slots : [null]
      }
      if (!refs.length && port !== 'in_reference_media') {
        const media = port === 'in_images' ? 'image' : port === 'in_reference_videos' ? 'video' : 'audio'
        const fallback = buildAssetReferencesFromEdges(nodes, [edge], targetId, port, media)
        return fallback.length ? fallback.map(ref => ref.mediaType) : [null]
      }
      if (!refs.length) return [null]
      const kinds = refs.map(ref => ref.mediaType)
      return edge.data?.fromIndex === undefined && (refs[0].fromIndex ?? 0) > 0
        ? [null, ...kinds] : kinds
    }))
}

export function buildReferenceMediaEdgeUpdates(refs: Array<Pick<CanvasAssetCandidate, 'nodeId' | 'nodeType' | 'assetPath' | 'mediaType' | 'sourceHandle' | 'fromIndex'>>): CanvasAssetEdgeUpdate[] {
  return refs.map((ref, toIndex) => {
    const build = ref.mediaType === 'image' ? buildImageReferenceEdgeUpdates
      : ref.mediaType === 'video' ? buildVideoReferenceEdgeUpdates : buildAudioReferenceEdgeUpdates
    const edge = build([ref])[0]
    return { ...edge, sourceHandle: ref.sourceHandle ?? edge.sourceHandle,
      ...(ref.fromIndex === undefined ? {} : { fromIndex: ref.fromIndex }), toIndex }
  })
}

/** Filter topology by source schema, including outputs that have not generated yet. */
export function isReferenceMediaEdge(edge: CanvasAssetEdge, nodes: MentionNode[], media: MediaType): boolean {
  if (edge.targetHandle !== 'in_reference_media' || !edge.sourceHandle) return false
  const source = nodes.find(node => node.id === edge.source)
  if (!source?.type) return false
  const spec = YDOC_NODE_FIELDS[FE_TYPE_TO_BACKEND[source.type] ?? source.type]?.[edge.sourceHandle]
  return spec?.baseType === `${media[0].toUpperCase()}${media.slice(1)}Asset`
}

export function replaceReferenceMediaEdges(
  canvas: { replacePortEdges: (target: string, port: string, edges: CanvasAssetEdgeUpdate[]) => void; transact?: (fn: () => void, origin?: 'frontend' | 'ref-sync') => void },
  target: string, refs: Parameters<typeof buildReferenceMediaEdgeUpdates>[0], legacyPorts: string[],
): void {
  const updates = buildReferenceMediaEdgeUpdates(refs)
  const save = () => {
    canvas.replacePortEdges(target, 'in_reference_media', updates)
    for (const port of legacyPorts) canvas.replacePortEdges(target, port, [])
  }
  if (canvas.transact) canvas.transact(save, 'ref-sync')
  else save()
}

/** Keep connections made through compatibility handles in the active reference list. */
export function referenceConnectionTargetHandle(
  targetType: string | undefined, targetId: string, targetHandle: string | undefined,
  edges: CanvasAssetEdge[],
): string | undefined {
  const unifiedPort = targetType && NODE_SCHEMAS_BY_TYPE[targetType]?.inputs.find(port =>
    port.referenceMediaRoutes && Object.values(port.referenceMediaRoutes).includes(targetHandle ?? ''),
  )
  if (unifiedPort && edges.some(edge => edge.target === targetId && edge.targetHandle === unifiedPort.id)) {
    return unifiedPort.id
  }
  return targetHandle
}

/** Upgrade topology before the first unified connection, including ungenerated outputs. */
export function buildReferenceMediaConnectionMigration(
  targetType: string | undefined, targetId: string, targetHandle: string | undefined,
  edges: CanvasAssetEdge[], connection: { source: string; sourceHandle?: string },
): { legacyPorts: string[]; updates: CanvasAssetEdgeUpdate[] } | undefined {
  const routes = targetType && NODE_SCHEMAS_BY_TYPE[targetType]?.inputs.find(port =>
    port.id === targetHandle,
  )?.referenceMediaRoutes
  if (!routes || edges.some(edge => edge.target === targetId && edge.targetHandle === targetHandle)) return
  // Match the legacy Config order, independently of generated object-key order.
  const legacyPorts = ['ImageAsset', 'VideoAsset', 'AudioAsset'].flatMap(type => routes[type] ? [routes[type]] : [])
  const legacyEdges = legacyPorts.flatMap(port => edges.filter(edge =>
    edge.target === targetId && edge.targetHandle === port,
  ).sort((a, b) => (a.data?.toIndex ?? 0) - (b.data?.toIndex ?? 0)))
  if (!legacyEdges.length) return
  const updates = [...legacyEdges, connection].map((edge, toIndex): CanvasAssetEdgeUpdate => {
    if (!edge.sourceHandle) throw new Error(`Reference connection from ${edge.source} requires an output handle`)
    const data = 'data' in edge ? edge.data : undefined
    return { source: edge.source, sourceHandle: edge.sourceHandle, toIndex,
      ...(data?.fromIndex === undefined ? {} : { fromIndex: data.fromIndex }),
      ...(data?.referencePairKind === undefined ? {} : { referencePairKind: data.referencePairKind }) }
  })
  return { legacyPorts, updates }
}
